import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { PageHeader } from '../../components/PageHeader';
import { isPlanError } from '../../components/plan/planErrors';
import { Upsell, UpsellFromError } from '../../components/plan/Upsell';
import { usePlan } from '../../components/plan/usePlan';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { PHONE_QUERY, useMediaQuery } from '../../lib/useMediaQuery';
import type { CommunityTemplateRead, CommunityZonesV2 } from '../../types/api';
import { useBrandIdentity } from '../knowledge-base/useBrandIdentity';
import { useSignedAssetUrl } from '../knowledge-base/useSignedAssetUrl';
import { useBusiness } from '../onboarding/useBusiness';
import { TextField } from './BuilderControls';
import { CommunityDesignBuilder } from './CommunityDesignBuilder';
import { CommunityDesignSource } from './CommunityDesignSource';
import {
  COMMUNITY_STATUS_BLURB,
  COMMUNITY_STATUS_LABEL,
  communityStatusTone,
  isCommunityTemplateEditable,
} from './communityStatus';
import { COMMUNITY_TEMPLATES_PATH, communityTemplatePath } from './communityTemplatePrefill';
import {
  createCommunityTemplate,
  deleteCommunityTemplate,
  fetchCommunityTemplate,
  submitCommunityTemplate,
  updateCommunityTemplate,
} from './communityTemplatesApi';
import {
  brandPalette,
  clampZones,
  defaultZones,
  upgradeZones,
  zoneProblems,
} from './communityZones';
import { StudioSmallScreen } from './StudioSmallScreen';
import { useFontCatalog } from './useFontCatalog';
import { useStartFromOwnTemplate } from './useStartFromOwnTemplate';

const TITLE_MAX_LENGTH = 80;
const DESCRIPTION_MAX_LENGTH = 300;

/**
 * The claim the author is making, word for word. It is the one thing standing
 * between the gallery and somebody else's paid template library, so it is a
 * real checkbox with a real sentence rather than a line of small print.
 */
const ATTESTATION_TEXT =
  "This design is my own original work and I have the right to share it. Templates exported from another platform's library may not be redistributed.";

export interface CommunityStudioDetailsProps {
  title: string;
  description: string;
  disabled: boolean;
  onTitle: (title: string) => void;
  onDescription: (description: string) => void;
}

/**
 * The template's own words under the builder, set in the builder's control
 * language: the title and description side by side, each with its counter.
 */
export function CommunityStudioDetails({
  title,
  description,
  disabled,
  onTitle,
  onDescription,
}: CommunityStudioDetailsProps) {
  return (
    <fieldset className="community-studio__details">
      <legend className="community-studio__details-title">Details</legend>
      <div className="community-studio__details-grid">
        <div className="community-studio__details-field">
          <TextField
            label="Title"
            maxLength={TITLE_MAX_LENGTH}
            counterLimit={TITLE_MAX_LENGTH}
            value={title}
            disabled={disabled}
            onChange={onTitle}
          />
          <p className="builder-hint">
            On your template list, and the gallery tile if you share it.
          </p>
        </div>
        <div className="community-studio__details-field">
          <TextField
            label="Description"
            rows={3}
            maxLength={DESCRIPTION_MAX_LENGTH}
            counterLimit={DESCRIPTION_MAX_LENGTH}
            placeholder="What this design is for, and the kind of post it suits."
            value={description}
            disabled={disabled}
            onChange={onDescription}
          />
          <p className="builder-hint">Optional.</p>
        </div>
      </div>
    </fieldset>
  );
}

/**
 * What the studio holds: the four editable fields, and nothing else. The map
 * is always v2 here — the studio only writes the current version.
 */
interface StudioForm {
  title: string;
  description: string;
  /**
   * The uploaded design, kept while the backdrop is a colour so switching back
   * to Image does not lose it. Only sent when the backdrop is an image.
   */
  image_path: string | null;
  zones: CommunityZonesV2;
}

/** A brand-new template's map. Built once per mount, not shared. */
const NEW_TEMPLATE_ZONES = defaultZones();

function formOf(template: CommunityTemplateRead | null): StudioForm {
  if (!template) {
    return { title: '', description: '', image_path: null, zones: NEW_TEMPLATE_ZONES };
  }
  return {
    title: template.title,
    description: template.description,
    image_path: template.image_path,
    // A v1 map opens as its v2 upgrade (the caption zone becomes one headline
    // box, the logo stays on), and every map is clamped on the way in as well
    // as on the way out: an older map, or one an operator repaired, still has
    // to be legal to edit.
    zones: clampZones(upgradeZones(template.zones)),
  };
}

/** Message for the domain errors this screen can provoke. */
function saveMessage(caught: unknown): string {
  if (caught instanceof ApiError) {
    switch (caught.code) {
      case 'REQUEST_NOT_EDITABLE':
        return 'This template is no longer editable — it is with the review team or already approved. Reload to see where it stands.';
      case 'RESOURCE_NOT_FOUND':
        return 'That template is no longer there.';
      default:
        return caught.message;
    }
  }
  return getErrorMessage(caught, 'That did not save. Try again.');
}

interface StudioProps {
  businessId: string;
  /** Null on `/templates/community/new` — nothing has been filed yet. */
  templateId: string | null;
}

function CommunityTemplateStudio({ businessId, templateId }: StudioProps) {
  const navigate = useNavigate();
  const [template, setTemplate] = useState<CommunityTemplateRead | null>(null);
  const [isLoading, setIsLoading] = useState(templateId !== null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // The user's own edits. Null until they touch something, so the form reads
  // straight off the loaded record and nothing is written to state in an effect.
  const [draft, setDraft] = useState<StudioForm | null>(null);
  const [attested, setAttested] = useState(false);
  const [zonesSuggested, setZonesSuggested] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<'save' | 'submit' | 'use' | 'delete' | null>(null);
  const [planError, setPlanError] = useState<ApiError | null>(null);
  // Making and editing an own template is Private templates. Without it the
  // studio is view only; deleting stays open, and an approved template can
  // still be used like any community one.
  const { can } = usePlan();
  const canPrivate = can('private_templates');
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const { start } = useStartFromOwnTemplate();
  // A phone gets the design read-only; editing it needs a larger screen.
  const isSmallScreen = useMediaQuery(PHONE_QUERY);

  useEffect(() => {
    if (!templateId) return;

    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const loaded = await fetchCommunityTemplate(businessId, templateId, controller.signal);
        if (cancelled) return;
        setTemplate(loaded);
        setLoadError(null);
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        setLoadError(getErrorMessage(caught, 'That template did not load. Reload to retry.'));
      } finally {
        if (!cancelled && !controller.signal.aborted) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [businessId, templateId]);

  const form = draft ?? formOf(template);
  const needsImage = form.zones.canvas.kind === 'image';
  // Checked against the catalogue once it has answered; until then the server decides.
  const fonts = useFontCatalog(form.zones.texts.some((text) => (text.font ?? null) !== null));
  const problems = [
    ...(needsImage && form.image_path === null
      ? ['An image backdrop needs a design: upload or import one, or pick a colour backdrop.']
      : []),
    ...zoneProblems(form.zones, fonts.status === 'ready' ? fonts.bySlug : null),
  ];
  const { identity } = useBrandIdentity(businessId);
  const palette = brandPalette(identity?.colors);
  const { url: logoUrl } = useSignedAssetUrl(identity?.logo_path ?? null);
  const design = useSignedAssetUrl(needsImage ? form.image_path : null);
  // Opened from the earlier studio: the map on screen is its upgrade, and the
  // first save is what writes it in the new format.
  const isUpgraded = template !== null && template.zones.v === 1;
  const isEditable =
    canPrivate && (template === null || isCommunityTemplateEditable(template.status));
  // Filling an own template that is not in the gallery is a private use.
  const canUse = canPrivate || template?.status === 'approved';
  // An existing template was attested when it was filed. Asking again would be
  // asking the author to re-sign something they already signed.
  const needsAttestation = template === null;
  const isBusy = busy !== null;
  // Edits on screen that the saved record does not have yet. A save resets the
  // draft to server truth, so equal-by-value is the honest test.
  const isDirty =
    template !== null &&
    draft !== null &&
    JSON.stringify(draft) !== JSON.stringify(formOf(template));

  function patch(changes: Partial<StudioForm>) {
    setFormError(null);
    setPlanError(null);
    setNotice(null);
    setDraft({ ...form, ...changes });
  }

  const readyToSave =
    isEditable &&
    !isBusy &&
    form.title.trim().length > 0 &&
    problems.length === 0 &&
    (!needsAttestation || attested);

  async function persist(): Promise<CommunityTemplateRead> {
    // A colour or gradient design carries no upload; an image one must.
    const imagePath = needsImage ? form.image_path : null;
    if (needsImage && imagePath === null) throw new Error('Add the design first.');
    const zones = clampZones(form.zones);

    const title = form.title.trim();
    const description = form.description.trim();

    if (template) {
      return updateCommunityTemplate(businessId, template.id, {
        title,
        description: description || null,
        image_path: imagePath,
        zones,
      });
    }
    return createCommunityTemplate(businessId, {
      title,
      description: description || null,
      image_path: imagePath,
      zones,
      // Literal `true`, and only reachable behind the ticked checkbox.
      attestation: true,
    });
  }

  async function handleSave(alsoSubmit: boolean) {
    if (!readyToSave) return;
    setFormError(null);
    setPlanError(null);
    setNotice(null);
    setBusy(alsoSubmit ? 'submit' : 'save');
    try {
      const saved = await persist();
      const settled = alsoSubmit ? await submitCommunityTemplate(businessId, saved.id) : saved;
      setTemplate(settled);
      // Back to server truth: a save is the moment the two agree.
      setDraft(formOf(settled));
      setZonesSuggested(false);
      setNotice(
        alsoSubmit
          ? 'Sent for review. You can keep posting on it while the review team decides whether to share it.'
          : 'Saved. You can post on it now; only you can see it until you submit it.',
      );
      if (!template) {
        // The draft is a real record now, so the URL says which one.
        void navigate(communityTemplatePath(settled.id), { replace: true });
      }
    } catch (caught) {
      if (isPlanError(caught)) setPlanError(caught);
      else setFormError(saveMessage(caught));
    } finally {
      setBusy(null);
    }
  }

  /**
   * Opens the brief on this template. What the brief uses is the saved record,
   * so unsaved edits are saved first; when they cannot be (a problem on the
   * list, no title) the button is off and the hint says why.
   */
  async function handleUse() {
    if (!template || isBusy || !canUse) return;
    if (isDirty && !readyToSave) return;
    setFormError(null);
    setPlanError(null);
    setNotice(null);
    setBusy('use');
    try {
      const saved = isDirty ? await persist() : template;
      if (isDirty) {
        setTemplate(saved);
        setDraft(formOf(saved));
      }
      await start(saved);
    } catch (caught) {
      if (isPlanError(caught)) setPlanError(caught);
      else setFormError(saveMessage(caught));
      setBusy(null);
    }
  }

  async function handleDelete() {
    if (!template) return;
    setBusy('delete');
    setFormError(null);
    try {
      await deleteCommunityTemplate(businessId, template.id);
      void navigate(COMMUNITY_TEMPLATES_PATH, { replace: true });
    } catch (caught) {
      setFormError(saveMessage(caught));
      setBusy(null);
      setIsConfirmingDelete(false);
    }
  }

  if (isLoading) {
    return (
      <p className="kb-section__status" role="status">
        Loading your template
      </p>
    );
  }

  if (loadError) {
    return (
      <div className="kb-section">
        <p className="form__error" role="alert">
          {loadError}
        </p>
        <Link className="button" to={COMMUNITY_TEMPLATES_PATH}>
          Back to my templates
        </Link>
      </div>
    );
  }

  return (
    <form
      className="form community-studio"
      onSubmit={(event) => {
        event.preventDefault();
        void handleSave(false);
      }}
      noValidate
    >
      {canPrivate ? null : <Upsell feature="private_templates" />}

      {template ? (
        <div className="community-status">
          <span className={communityStatusTone(template.status)}>
            {COMMUNITY_STATUS_LABEL[template.status]}
          </span>
          <span className="community-status__blurb">{COMMUNITY_STATUS_BLURB[template.status]}</span>
        </div>
      ) : null}

      {template && template.status === 'rejected' && template.review_note ? (
        <div className="community-review-note">
          <span className="community-review-note__label">What to change</span>
          <p className="community-review-note__text">{template.review_note}</p>
        </div>
      ) : null}

      {isUpgraded ? (
        <p className="form__notice" role="status">
          This template was made in the earlier studio. It opens here with its caption as one
          headline box and the logo where it was; saving stores it in the new format.
        </p>
      ) : null}

      {zonesSuggested ? (
        <p className="form__notice" role="status">
          The text boxes below were suggested from your document. Check them against the design
          before you save — a suggestion is a starting point, not a reading of what you meant.
        </p>
      ) : null}

      {isSmallScreen ? (
        <StudioSmallScreen
          zones={form.zones}
          palette={palette}
          imageUrl={design.url}
          isLoadingImage={design.isLoading}
          logoUrl={logoUrl}
        />
      ) : (
        <CommunityDesignBuilder
          zones={form.zones}
          palette={palette}
          imageUrl={design.url}
          isLoadingImage={design.isLoading}
          logoUrl={logoUrl}
          disabled={!isEditable || isBusy}
          onChange={(zones) => patch({ zones })}
          imageSource={
            <CommunityDesignSource
              businessId={businessId}
              imagePath={form.image_path}
              disabled={!isEditable || isBusy}
              onPicked={(imagePath, suggested) => {
                setZonesSuggested(suggested !== null);
                // A picked design is an image backdrop; a suggestion read off a
                // document replaces the layers, in its v2 form.
                patch({
                  image_path: imagePath,
                  zones: suggested
                    ? clampZones({ ...upgradeZones(suggested), canvas: { kind: 'image' } })
                    : { ...form.zones, canvas: { kind: 'image' } },
                });
              }}
            />
          }
        />
      )}

      <CommunityStudioDetails
        title={form.title}
        description={form.description}
        disabled={!isEditable || isBusy}
        onTitle={(title) => patch({ title })}
        onDescription={(description) => patch({ description })}
      />

      {needsAttestation ? (
        <label className="community-attestation">
          <input
            type="checkbox"
            checked={attested}
            disabled={isBusy || !canPrivate}
            onChange={(event) => {
              setFormError(null);
              setAttested(event.target.checked);
            }}
          />
          <span className="community-attestation__text">{ATTESTATION_TEXT}</span>
        </label>
      ) : null}

      {problems.length > 0 ? (
        <ul className="community-problems" role="status">
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      ) : null}

      {formError ? (
        <p className="form__error" role="alert">
          {formError}
        </p>
      ) : null}

      <UpsellFromError error={planError} />

      {notice ? (
        <p className="form__notice" role="status">
          {notice}
        </p>
      ) : null}

      <div className="community-studio__actions">
        <button className="button button--primary" type="submit" disabled={!readyToSave}>
          {busy === 'save' ? 'Saving' : template ? 'Save changes' : 'Save draft'}
        </button>
        <button
          className="button"
          type="button"
          disabled={!readyToSave}
          onClick={() => void handleSave(true)}
        >
          {busy === 'submit' ? 'Sending' : 'Submit for review'}
        </button>
        {template ? (
          <button
            className="button"
            type="button"
            disabled={isBusy || !canUse || (isDirty && !readyToSave)}
            onClick={() => void handleUse()}
          >
            {busy === 'use' ? 'Opening' : isDirty ? 'Save and use' : 'Use this template'}
          </button>
        ) : null}
        {template ? (
          <button
            className="button button--ghost button--danger-text"
            type="button"
            disabled={isBusy}
            onClick={() => setIsConfirmingDelete(true)}
          >
            Delete
          </button>
        ) : null}
        <Link className="button button--ghost" to={COMMUNITY_TEMPLATES_PATH}>
          My templates
        </Link>
      </div>

      <p className="field__hint">
        {template
          ? isDirty && !readyToSave
            ? 'Fix what is listed above and save before you use it: posts use the saved design.'
            : 'You can use this template in a post straight away, whatever its status. Submit for review only to share it with every member; while the review team has it, it cannot be edited.'
          : 'Save it and you can use it in a post straight away. Submit for review only to share it with every member; while the review team has it, it cannot be edited.'}
      </p>

      {isConfirmingDelete && template ? (
        <ConfirmDialog
          title="Delete this template"
          message={`"${template.title}" and its zone map are removed. Posts already made on it are not affected.`}
          confirmLabel="Delete template"
          busyLabel="Deleting"
          isBusy={busy === 'delete'}
          onConfirm={() => void handleDelete()}
          onDismiss={() => setIsConfirmingDelete(false)}
        />
      ) : null}
    </form>
  );
}

/**
 * The community template studio (`/templates/community/new` and
 * `/templates/community/:templateId`).
 *
 * A template is a finished design plus a map of where the dynamic layers land
 * on it. The author owns both, and may post on it the moment it is saved, in
 * any status. The review team owns only whether it is shared with everyone —
 * so this screen is honest about which half is theirs: everything is editable
 * while the template is a draft or came back with a note, and read-only the
 * moment it is with the reviewers or live in the gallery.
 */
export function CommunityTemplateStudioPage() {
  const { businessId, isLoading, error } = useBusiness();
  const { templateId } = useParams();

  return (
    <main className="page page--wide">
      <PageHeader
        section="Templates / Community"
        title={templateId ? 'Edit your template' : 'Create a community template'}
        lede="Build a design from a backdrop, shapes and text, or your own artwork. Yours to post once saved; nothing is redrawn."
      />

      {isLoading ? (
        <p className="kb-section__status" role="status">
          Loading your business
        </p>
      ) : !businessId ? (
        <div className="kb-section">
          <p className="form__notice">
            {error ??
              'No business is linked to this account yet. Finish setup and the studio opens.'}
          </p>
          <Link className="button" to="/onboarding">
            Finish setup
          </Link>
        </div>
      ) : (
        <CommunityTemplateStudio businessId={businessId} templateId={templateId ?? null} />
      )}
    </main>
  );
}
